import { ToastContainer, toast } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

export type AlertVariant = "info" | "warn" | "success" | "error";

export type ALERT = {
  title: string;
  variant: AlertVariant;
  onClose?: () => void;
  active: boolean;
};

type Props = {
  alert: ALERT;
};

/**
 * Fires a toast straight away. Called from `useAlert().open()` rather than
 * from an effect, because most callers close or unmount themselves in the
 * same handler (e.g. a profile editor calling `callback()` on success) and an
 * effect on an unmounted component never runs.
 */
export const showAlert = (title: string, variant: AlertVariant) => {
  if (!title) return;

  if (variant === "success") {
    toast.success(title, { className: "!text-green-500 !bg-gray-700" });
  } else if (variant === "error") {
    toast.error(title, { className: "!text-red-500 !bg-gray-700" });
  } else {
    toast.info(title, { className: "!text-blue-500 !bg-gray-700" });
  }
};

/**
 * The app's single toast container, mounted once in App. It used to be
 * rendered by each <Alert />, so a toast vanished whenever the component that
 * raised it closed.
 */
export const AppToastContainer = () => (
  <ToastContainer
    position="top-right"
    // Sit just below the h-14 Navbar instead of over the bell and avatar.
    style={{ top: "calc(3.5rem + 0.75rem)" }}
    autoClose={10000}
    hideProgressBar={false}
    newestOnTop={false}
    closeOnClick
    rtl={false}
    pauseOnFocusLoss
    draggable
    pauseOnHover
  />
);

/**
 * Kept so existing `<Alert />` call sites still compile. Toasts are raised by
 * `showAlert` and rendered by `AppToastContainer`, so this renders nothing.
 */
const Alert = (_props: Props) => null;

export default Alert;
